import type { Abi, Address } from "viem"
import type { Config } from "wagmi"

import { thickV2NfpPositionIncreaseLiquidityAbi } from "@/domains/abis/thick-v2-nfpm-abi"
import type { TransactionReturnType } from "@/domains/on-chain"
import { simulatedTransaction } from "@/domains/on-chain/simulated-transaction"

interface IncreaseThickNfpPositionProps {
  account: Address
  chainId: number
  config: Config
  tokenId: bigint
  amount0Desired: bigint
  amount1Desired: bigint
  amount0Min: bigint
  amount1Min: bigint
  deadline: number
  nfpManagerAddress: Address
}

/**
 * Increase liquidity on a thick-v2 position. The NFPM resolves pool + fee
 * internally from `tokenId`, so the encoder does not need a `ThickV2PoolKey`.
 */
export const increaseThickNfpPosition = async ({
  account,
  chainId,
  config,
  tokenId,
  amount0Desired,
  amount1Desired,
  amount0Min,
  amount1Min,
  deadline,
  nfpManagerAddress,
}: IncreaseThickNfpPositionProps): Promise<TransactionReturnType> => {
  try {
    const params = {
      tokenId,
      amount0Desired,
      amount1Desired,
      amount0Min,
      amount1Min,
      deadline,
    }

    return await simulatedTransaction({
      config,
      simulateArgs: {
        abi: thickV2NfpPositionIncreaseLiquidityAbi as Abi,
        account,
        address: nfpManagerAddress,
        args: [params],
        chainId,
        functionName: "increaseLiquidity",
      },
    })
  } catch (error: unknown) {
    console.error("increaseThickNfpPosition", error)
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error",
      code: error instanceof Error && "code" in error ? Number((error as { code: string }).code) : 0,
    }
  }
}
